import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhoneInputComponent from "./PhoneInputComponent";
import { createInStayRentalOrderAPI } from "../API/InStayRentalAPI";
import CustomBusinessServiceLoader from "./Loaders/CustomBusinessServiceLoader";
import { useBusinessNameContext } from "../context/BusinessName";
import Currency from "./Currency";
import LocationVerificationModal from "./LocationVerificationModal";
import InStayRentalPriceBreakdown from "./InStayRentalPriceBreakdown";
import { getLocalDateString } from "../utils/date";
import {
  buildRentalBookingFields,
  buildRentalOrderFields,
  calculateRentalQuote,
  formatRentalDateTime,
  getApiErrorDetails,
  getRentalFrequency,
  getRentalInputFields,
  getRequestErrorDetails,
  getUnassignedErrorMessage,
  hasRentalPrice,
  isPastRentalStart,
  parseRentalDuration,
} from "../utils/inStayRental";
import PaymentOptionList from "./PaymentOptionList";
import { normalizePaymentMethods } from "../utils/paymentMethods";

const formatLabel = (value) =>
  String(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const CONTACT_FIELDS = ["name", "phone", "email"];

// Durations are whole numbers, so keep signs, exponents and decimals out of the input.
const blockNonIntegerKeys = (e) => {
  if (["-", "+", "e", "E", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

function InStayRentalBookModal({ item, room_uuid, onClose }) {
  const { businessName } = useBusinessNameContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = sessionStorage.getItem("lang");

  const frequency = getRentalFrequency(item?.payment_frequency);
  const isDurationBased = Boolean(frequency?.durationBased);
  const needsStartTime = Boolean(frequency?.withStartTime);
  const bookingInputFields = getRentalInputFields(frequency);

  const today = getLocalDateString();

  const getMinStartDate = () => {
    const minDate = new Date();

    minDate.setDate(minDate.getDate() + Number(item?.lead_time_in_days || 0));

    return getLocalDateString(minDate);
  };

  const minStartDate = getMinStartDate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dateInput, setDateInput] = useState(minStartDate);
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("1");
  const [loading, setLoading] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const [nameErr, setNameErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [phoneWithoutCode, setPhoneWithoutCode] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [sDateErr, setSDateErr] = useState("");
  const [startTimeErr, setStartTimeErr] = useState("");
  const [durationErr, setDurationErr] = useState("");
  const [orderErr, setOrderErr] = useState("");

  // The item can refresh while the modal is open (React Query refetches it on
  // window focus), so a start date still on the old earliest date, or one the
  // new lead time no longer allows, moves to the new earliest date.
  const previousMinStartDateRef = useRef(minStartDate);

  useEffect(() => {
    const previousMinStartDate = previousMinStartDateRef.current;

    if (previousMinStartDate === minStartDate) return;

    previousMinStartDateRef.current = minStartDate;
    setDateInput((current) =>
      !current || current === previousMinStartDate || current < minStartDate
        ? minStartDate
        : current
    );
    setSDateErr("");
  }, [minStartDate]);

  const hasLeadTime = Number(item?.lead_time_in_days) > 0;
  const durationValue = parseRentalDuration(duration);
  const isStartDateInvalid =
    isDurationBased &&
    (!dateInput ||
      dateInput < today ||
      (hasLeadTime && dateInput < minStartDate));
  const isStartInPast =
    needsStartTime &&
    Boolean(dateInput && startTime) &&
    isPastRentalStart(dateInput, startTime);
  const isStartTimeInvalid = needsStartTime && (!startTime || isStartInPast);
  const isDurationInvalid = isDurationBased && !durationValue;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedOnlinePaymentMethod, setSelectedOnlinePaymentMethod] =
    useState("credit card");

  useEffect(() => {
    const methods = normalizePaymentMethods(item?.payment_methods);

    setPaymentMethods(methods);
    setSelectedPaymentMethod(methods[0]?.payment_method || "");
  }, [item]);

  const bookingFields =
    frequency &&
    item?.id != null &&
    !isStartDateInvalid &&
    !isStartTimeInvalid &&
    !isDurationInvalid
      ? buildRentalBookingFields({
          itemId: item.id,
          frequency,
          startDate: dateInput,
          startTime,
          duration: durationValue,
        })
      : null;

  // The key identifies the selection together with the price and deposit it is
  // priced from, so refreshed item data invalidates an order already confirmed.
  const booking = bookingFields && {
    key: JSON.stringify([bookingFields, item.price, item.security_deposit]),
    fields: bookingFields,
    frequency,
    unitPrice: item.price,
    deposit: item.security_deposit,
  };

  // The price comes from the item itself, so there is nothing to wait for.
  const quote = booking && calculateRentalQuote(booking);

  // The order is sent after location verification, so it is checked against
  // the latest selection through refs rather than render-time values.
  const latestBookingKeyRef = useRef(null);
  const confirmedOrderRef = useRef(null);
  latestBookingKeyRef.current = booking?.key ?? null;

  const currency = quote?.currency || localStorage.getItem("currency");
  const canBook = Boolean(frequency) && hasRentalPrice(item?.price);

  const startTimeError =
    startTimeErr ||
    (isStartInPast ? t("Start time cannot be in the past.") : "");

  const endDateText = quote
    ? formatRentalDateTime(quote.endDate, quote.endTime)
    : "—";

  const handleDate = (e) => {
    const selectedDate = e.target.value;

    setDateInput(selectedDate);
    setOrderErr("");

    if (!selectedDate) {
      setSDateErr("");
      return;
    }

    if (hasLeadTime && selectedDate < minStartDate) {
      setSDateErr(
        t(
          Number(item.lead_time_in_days) === 1
            ? "lead_time_validation_one_day"
            : "lead_time_validation",
          {
            days: item.lead_time_in_days,
          }
        )
      );
      return;
    }

    if (selectedDate < today) {
      setSDateErr(t("Start date cannot be in the past."));
      return;
    }

    setSDateErr("");
  };

  const handleStartTime = (e) => {
    setStartTime(e.target.value);
    setStartTimeErr("");
    setOrderErr("");
  };

  const handleDuration = (e) => {
    const value = e.target.value;

    setDuration(value);
    setOrderErr("");
    setDurationErr(
      value === "" || parseRentalDuration(value)
        ? ""
        : t("Please enter a whole number greater than zero.")
    );
  };

  const isEmailValid = /\S+@\S+\.\S+/.test(email);

  const checkInputs = () => {
    if (name === "") {
      setNameErr("The name field is required.");
    } else {
      setNameErr("");
    }
    if (phoneWithoutCode.trim() === "") {
      setPhoneErr("The phone field is required.");
    } else {
      setPhoneErr("");
    }
    if (email === "") {
      setEmailErr("The email field is required.");
    } else if (!isEmailValid) {
      setEmailErr("The email must be a valid email address.");
    } else {
      setEmailErr("");
    }
  };

  const showOrderErrors = (error) => {
    const setters = {
      name: setNameErr,
      phone: setPhoneErr,
      email: setEmailErr,
      from_date: setSDateErr,
      from_time: setStartTimeErr,
      duration: setDurationErr,
    };
    const inputFields = [...CONTACT_FIELDS, ...bookingInputFields];

    Object.entries(error.fieldErrors).forEach(([field, message]) => {
      if (inputFields.includes(field)) {
        setters[field](message);
      }
    });

    setOrderErr(
      getUnassignedErrorMessage(
        error,
        inputFields,
        t("Something went wrong. Please try again.")
      )
    );
  };

  const submitOrder = async () => {
    const order = confirmedOrderRef.current;

    // Never book a calculation the guest has changed since confirming it.
    if (!order || order.booking.key !== latestBookingKeyRef.current) {
      setOrderErr(
        t(
          "Your booking details changed. Please review the updated price and confirm again."
        )
      );
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", order.name);
    formData.append("phone", order.phone);
    formData.append("email", order.email);
    formData.append("payment_method", order.paymentMethod);
    Object.entries(
      buildRentalOrderFields(order.booking.fields, order.quote)
    ).forEach(([field, value]) => formData.append(field, value));

    try {
      const res = await createInStayRentalOrderAPI({
        room_uuid,
        formData,
        lang,
      });

      if (res.data.status || res.data.success) {
        navigate(`/${room_uuid}/InStayRentalOrderPlaced`, {
          state: {
            metadata: {
              res: res.data.data,
              item: {
                name: item?.name,
                image: item?.image,
              },
              booking: { ...order.quote, currency: order.currency },
            },
          },
        });
        return;
      }

      showOrderErrors(getApiErrorDetails(res.data));
    } catch (err) {
      console.log("err", err);
      showOrderErrors(getRequestErrorDetails(err));
    } finally {
      setLoading(false);
    }
  };
  const handleOrder = () => {
    checkInputs();
    setOrderErr("");

    if (!quote) return;

    if (name !== "" && phoneWithoutCode.trim() !== "" && isEmailValid) {
      confirmedOrderRef.current = {
        booking,
        quote,
        currency,
        name,
        phone,
        email,
        paymentMethod: selectedPaymentMethod,
      };
      setShowLocationModal(true);
    }
  };
  const handleLocationVerified = () => {
    setShowLocationModal(false);
    submitOrder();
  };
  if (loading) {
    return (
      <CustomBusinessServiceLoader businessName={businessName} loader={"spa"} />
    );
  }

  return (
    <>
      <div className="modal-item">
        <div className="car-img-container w-100">
          {item?.image && <img src={item.image} alt={item.name} />}
        </div>
        <div className="txt-pnl" style={{ marginBottom: "1rem" }}>
          <h2>{item?.name}</h2>
          {/* {item?.description && <p>{item.description}</p>} */}
        </div>
      </div>

      {(item?.payment_frequency || item?.price) && (
        <>
          <div className="full-heading flex grey-bg m-0">
            <h3>{t("Details")}</h3>
          </div>
          <div className="check-list-container">
            <ul className="check-list v3">
              {item?.description && (
                <li>
                  <p>{t("Description")}</p>
                  <p>{item.description}</p>
                </li>
              )}
              {Number(item?.lead_time_in_days) > 0 && (
                <li>
                  <p>{t("Lead Time")}</p>

                  <p>
                    {item?.lead_time_in_days
                      ? `${item.lead_time_in_days} ${t(
                          item.lead_time_in_days === 1 ? t("day") : t("days")
                        )} `
                      : ""}

                    {/* {item?.lead_time_in_hours
                      ? `${item.lead_time_in_hours} ${t(
                          item.lead_time_in_hours === 1 ? "hour" : "hours"
                        )} `
                      : ""}

                    {item?.lead_time_in_minutes
                      ? `${item.lead_time_in_minutes} ${t(
                          item.lead_time_in_minutes === 1 ? "minute" : "minutes"
                        )}`
                      : ""} */}
                  </p>
                </li>
              )}
              {item?.payment_frequency && (
                <li>
                  <p>{t("Payment Frequency")}</p>
                  <p>{t(formatLabel(item.payment_frequency))}</p>
                </li>
              )}
              {item?.security_deposit > 0 && (
                <li>
                  <p>{t("Security Deposit")}</p>
                  <p>
                    {item.security_deposit}{" "}
                    <Currency currency={localStorage.getItem("currency")} />
                  </p>
                </li>
              )}
              {item?.price && (
                <li>
                  <p>{t("Price")}</p>
                  <p>
                    {item.price}{" "}
                    <Currency currency={localStorage.getItem("currency")} />
                  </p>
                </li>
              )}
            </ul>
          </div>
        </>
      )}

      <div className="full-heading flex grey-bg m-0">
        <h3>{t("Your Information")}</h3>
      </div>
      <div className="full-heading m-0">
        <p style={{ marginBottom: 5 }}>
          {t("Enter your information to complete the request")}
        </p>
      </div>

      <div className="car-input-container">
        <Row>
          <Col xs="12">
            <Form.Group className="mb-3 p" controlId="formName">
              <Form.Control
                type="text"
                className="pl-5"
                placeholder={t("Your Name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>
            {nameErr !== "" && <span className="err_msg">{t(nameErr)}</span>}
          </Col>
          <Col xs="12">
            <Form.Group className="mb-3 p" controlId="formEmail">
              <Form.Control
                type="email"
                className="pl-5"
                placeholder={t("Your Email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
            {emailErr !== "" && <span className="err_msg">{t(emailErr)}</span>}
          </Col>
        </Row>

        <Form.Group className="mb-3 w-100">
          <PhoneInputComponent
            value={phone}
            onChange={(phone, country) => {
              setPhone(phone);

              const numberWithoutCode = phone.slice(
                country?.dialCode?.length || 0
              );

              setPhoneWithoutCode(numberWithoutCode);
            }}
          />
        </Form.Group>
        {phoneErr !== "" && <span className="err_msg">{t(phoneErr)}</span>}

        {isDurationBased && (
          <>
            <div className="bk-date">
              <Form.Group
                className="mb-3 row align-items-center justify-content-start"
                controlId="rentalStartDate"
              >
                <p className="col-3 m-0 f-12 ">{t("Start Date")} </p>
                <Form.Control
                  type="date"
                  value={dateInput}
                  onChange={handleDate}
                  min={today}
                  className="col-8 ml-2  dt-input"
                />
              </Form.Group>
              {sDateErr !== "" && <span className="err_msg">{sDateErr}</span>}
            </div>
            {needsStartTime && (
              <div className="bk-time">
                <Form.Group
                  className="mb-3 row align-items-center justify-content-start"
                  controlId="rentalStartTime"
                >
                  <p className="col-3 m-0 f-12 no-break">{t("Start Time")} </p>
                  <Form.Control
                    type="time"
                    value={startTime}
                    onChange={handleStartTime}
                    className="col-8 ml-2  dt-input"
                  />
                </Form.Group>
                {startTimeError !== "" && (
                  <span className="err_msg">{startTimeError}</span>
                )}
              </div>
            )}
            <div className="bk-time">
              <Form.Group
                className="mb-3 row align-items-center justify-content-start"
                controlId="rentalDuration"
              >
                <p className="col-3 m-0 f-12 ">{t(frequency.durationLabel)} </p>
                <Form.Control
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={duration}
                  onChange={handleDuration}
                  onKeyDown={blockNonIntegerKeys}
                  className="col-8 ml-2  dt-input"
                />
              </Form.Group>
              {durationErr !== "" && (
                <span className="err_msg">{durationErr}</span>
              )}
            </div>
            <div className="bk-time mb-4">
              <div className="mb-3 row align-items-center justify-content-start">
                <p className="col-3 m-0 f-12 ">
                  {t(needsStartTime ? "End Date & Time" : "End Date")}{" "}
                </p>
                <p
                  className="col-8 ml-2 m-0 rental-readonly-value"
                  aria-live="polite"
                >
                  {endDateText}
                </p>
              </div>
            </div>
          </>
        )}

        {frequency && !isDurationBased && (
          <div className="bk-date mb-4">
            <div className="mb-3 row align-items-center justify-content-start">
              <p className="col-3 m-0 f-12 ">{t("Duration")} </p>
              <p className="col-8 ml-2 m-0 rental-readonly-value">
                {t("For your entire stay")}
              </p>
            </div>
          </div>
        )}

        {!canBook && (
          <span className="err_msg mt-0 mb-4">
            {t("This item can't be booked right now.")}
          </span>
        )}
      </div>

      {canBook && (
        <>
          <div className="full-heading flex grey-bg m-0">
            <h3>{t("Price Details")}</h3>
          </div>
          <div className="check-list-container" aria-live="polite">
            {!booking && (
              <p className="rental-price-status">
                {t("Complete your booking details to see the price.")}
              </p>
            )}
            {booking && !quote && (
              <p className="rental-price-status">
                <span className="text-danger">
                  {t("Please review your booking details above.")}
                </span>
              </p>
            )}
            {quote && (
              <InStayRentalPriceBreakdown quote={quote} currency={currency} />
            )}
          </div>
        </>
      )}
      {paymentMethods.length > 0 && (
        <div className="mb-4">
          <div className="full-heading payment flex">
            <h2>{t("Payment Options")}</h2>
          </div>
          <PaymentOptionList
            selected={selectedPaymentMethod}
            setSelected={setSelectedPaymentMethod}
            methods={paymentMethods}
            setSelectedOnlinePaymentMethod={setSelectedOnlinePaymentMethod}
          />
        </div>
      )}
      <div className="check-list-container">
        <div className="full-div padd-div text-center">
          {orderErr !== "" && (
            <span className="err_msg mt-0 mb-3">{orderErr}</span>
          )}
          <Button
            className="reg-btn big"
            type="button"
            onClick={handleOrder}
            disabled={loading || !quote}
          >
            {t("Book Now")}
          </Button>
        </div>
      </div>
      <LocationVerificationModal
        show={showLocationModal}
        room_uuid={room_uuid}
        onVerified={handleLocationVerified}
      />
    </>
  );
}

export default InStayRentalBookModal;
